import React, { useEffect, useState } from "react";
import TradeNav from "@/components/Table/TradeNav";
import { IAsset, IVWPost } from "@/interfaces";
import { autorun } from "mobx";
import { inject, observer } from "mobx-react";
import Store from "@/store";
import { TradingStore } from "@/store/Trading";
import TradeTable from "@/components/Table/Trade";
import {
  MenuItem,
  Paper,
  Typography,
  Grid,
  Stack,
  useTheme,
  TextField,
} from "@mui/material";
import CommonStore from "@/store/common";
import { useLocation, useNavigate } from "react-router-dom";
import { LoadingButton as Button } from "@mui/lab";
import { TradeForm } from "@/components/Modal";

const TradePage = inject(
  Store.type.tradingStore,
  Store.type.commonStore,
)(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  observer((props: any) => {
    const tradingStore = props[Store.type.tradingStore] as TradingStore;
    const commonStore = props[Store.type.commonStore] as CommonStore;
    //get query params
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get("tab");

    const theme = useTheme();
    const navigate = useNavigate();

    const [side, setSide] = useState<"sell" | "buy">(
      tab === "sell" ? "sell" : "buy",
    );
    const [postsPageBuy, setPostsPageBuy] = useState<IVWPost[]>([]);
    const [postsPageSell, setPostsPageSell] = useState<IVWPost[]>([]);
    const [tradeList, setTradeList] = useState<IVWPost[]>([]);
    const [selectPost, setSelectPost] = useState<IVWPost | null>(null);

    const [assetList, setAssetList] = useState<IAsset[]>([]);
    const [asset, setAsset] = useState<string | "null">("null");

    useEffect(() => {
      let unsubscribeOpenPost: () => void;
      commonStore.fetchAssets().catch((error) => {
        console.error("Error assets:", error);
      });
      (async () => {
        unsubscribeOpenPost = await tradingStore.subscribeToOpenPosts();
      })();
      const disposeAutorun = autorun(async () => {
        const assets = commonStore.assets;
        const BuyList = tradingStore.postsOpenBuy;
        const SellList = tradingStore.postsOpenSell;
        if (BuyList && SellList) {
          setPostsPageBuy(SellList);
          setPostsPageSell(BuyList);
        }
        if (assets) {
          setAssetList(assets);
        }
      });
      return () => {
        disposeAutorun();
        if (unsubscribeOpenPost) {
          unsubscribeOpenPost();
        }
      };
    }, [commonStore, tradingStore]);

    useEffect(() => {
      let posts;
      if (side === "buy") {
        posts = postsPageBuy;
      } else if (side === "sell") {
        posts = postsPageSell;
      }
      if (posts) {
        if (asset !== "null") {
          posts = posts.filter((post) => post.asset_id === asset);
        }
        setTradeList(posts);
      }
    }, [side, postsPageBuy, asset]);

    return (
      <>
        <Paper
          sx={{
            m: "82.5px 78px 50.5px 56px",
            p: "68px 54px 50px 64px",
            minHeight: `calc((100vh - ${theme.navbarHeight} - ${theme.footerHeight} - 82.5px - 50.5px))`,
          }}
        >
          <Grid
            container
            spacing={1}
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            alignContent="stretch"
            wrap="wrap"
          >
            <Grid item sm={4}></Grid>
            <Grid item sm={4}>
              <Typography variant="h3" textAlign="center">
                Trade
              </Typography>
            </Grid>
            <Grid item sm={4}>
              <Button
                onClick={() => navigate("/trade/create-post")}
                variant="outlined"
                color="primary"
                size="large"
                sx={{ float: "right" }}
              >
                Create post
              </Button>
            </Grid>
          </Grid>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            mt="71px"
            mb="93px"
          >
            <TradeNav side={side} setSide={setSide} />
            <Stack direction="row" alignItems="center" gap="15px">
              <TextField
                label="Asset"
                type="text"
                name="asset"
                id="asset"
                select
                value={asset === "null" ? "null" : asset}
                onChange={(e) => setAsset(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none !important",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline, .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      border: "none !important",
                    },
                }}
              >
                <MenuItem value={"null"}>All</MenuItem>
                {assetList.map((pm) => (
                  <MenuItem key={pm.asset_id} value={pm.asset_id}>
                    {pm.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>
          <TradeTable tradeList={tradeList} setSelectedPost={setSelectPost} />
        </Paper>
        {selectPost && (
          <TradeForm
            selectPost={selectPost}
            setSelectedPost={setSelectPost}
          ></TradeForm>
        )}
      </>
    );
  }),
);

export default TradePage;
